import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Archive,
  FolderOpen,
  Folder,
  Plus,
  Trash2,
  Eye,
  FileText,
  AlertTriangle,
  Info,
  Sparkles,
} from 'lucide-react';
import {
  classifyDistributionScoreRevert,
  revertDistributionDeltaFromJudges,
  revertFolderDistributionsFromJudges,
} from '../utils/judgeScoreDelta';
import {
  pickInitialActiveFolderId,
  persistActiveFolderId,
  readActiveFolderId,
  subscribeActiveFolderChanges,
} from '../utils/activeFolderStorage';

const SavedDistributions = () => {
  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolderState] = useState(null);
  const [newFolderName, setNewFolderName] = useState('');
  const navigate = useNavigate();

  // Public setter — state + localStorage + cross-component senkronizasyonu için
  // custom event. useDistributionFolders aynı event'i dinler.
  const setActiveFolder = (folderId) => {
    setActiveFolderState(folderId);
    persistActiveFolderId(folderId);
  };

  useEffect(() => {
    loadFolders();
    // Başka komponent (FileManagement / hook) aktif klasörü değiştirdiğinde
    // bu sayfa da senkron olsun.
    const unsubscribe = subscribeActiveFolderChanges(() => {
      setActiveFolderState((prev) => {
        const next = readActiveFolderId();
        return prev === next ? prev : next;
      });
    });
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- yalnızca mount’ta localStorage okuma
  }, []);

  const loadFolders = () => {
    const foldersString = localStorage.getItem('distributionFolders');
    if (foldersString) {
      const loadedFolders = JSON.parse(foldersString);
      setFolders(loadedFolders);
      setActiveFolderState(pickInitialActiveFolderId(loadedFolders));
    } else {
      createDefaultFolder();
    }
  };

  const createDefaultFolder = () => {
    const currentDate = new Date();
    const monthNames = [
      'Ocak',
      'Şubat',
      'Mart',
      'Nisan',
      'Mayıs',
      'Haziran',
      'Temmuz',
      'Ağustos',
      'Eylül',
      'Ekim',
      'Kasım',
      'Aralık',
    ];
    const folderName = `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    const newFolder = {
      id: Date.now(),
      name: folderName,
      distributions: [],
    };
    setFolders([newFolder]);
    setActiveFolder(newFolder.id);
    localStorage.setItem('distributionFolders', JSON.stringify([newFolder]));
  };

  const handleAddFolder = () => {
    if (!newFolderName.trim()) return;
    const newFolder = {
      id: Date.now(),
      name: newFolderName.trim(),
      distributions: [],
    };
    const updatedFolders = [...folders, newFolder];
    setFolders(updatedFolders);
    setActiveFolder(newFolder.id);
    localStorage.setItem('distributionFolders', JSON.stringify(updatedFolders));
    setNewFolderName('');
  };

  const removeFolderFromState = (folderId) => {
    const updatedFolders = folders.filter((folder) => folder.id !== folderId);
    setFolders(updatedFolders);
    if (activeFolder === folderId) {
      setActiveFolder(updatedFolders[updatedFolders.length - 1]?.id || null);
    }
    localStorage.setItem('distributionFolders', JSON.stringify(updatedFolders));
  };

  const handleDeleteFolderAndRevertScores = (folderId) => {
    const folder = folders.find((f) => f.id === folderId);
    if (!folder) return;

    const list = folder.distributions || [];
    if (list.length === 0) {
      if (
        window.confirm(
          'Bu klasör boş. Klasörü silmek istediğinizden emin misiniz?'
        )
      ) {
        removeFolderFromState(folderId);
      }
      return;
    }

    for (let i = 0; i < list.length; i += 1) {
      const d = list[i];
      if (classifyDistributionScoreRevert(d) === 'none') {
        window.alert(
          `Klasördeki "${d.fileName || `Kayıt #${i + 1}`}" için puan etkisi hesaplanamıyor. Önce bu kaydı tek tek inceleyin veya yalnızca "Kayıt sil" ile kaldırın.`
        );
        return;
      }
    }

    const hasScoreRevert =
      list.some((d) => classifyDistributionScoreRevert(d) === 'revert');

    const confirmMessage = hasScoreRevert
      ? 'Bu klasör ve içindeki tüm tevzi kayıtları silinecek. Genel hakim listesine işlenmiş kayıtların puan artışları ve bu tevzilerde atanan dosya adetleri, kayıt sırasının tersine (önce son tevzi) mevcut hakim puanlarından ve dosya sayılarından düşülecek. Yalnızca listeye işlenmemiş kayıtlar için puan ve dosya sayısı düşmez.\n\nDevam edilsin mi?'
      : 'Bu klasörde genel hakim listesine işlenmiş tevzi yok. Klasör ve kayıtlar silinsin mi? Hakim puanları ve dosya sayıları değişmez.';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    const judgesRaw = localStorage.getItem('judges');
    if (!judgesRaw) {
      window.alert('Kayıtlı hakim listesi bulunamadı; puan güncellenemedi.');
      return;
    }

    let judgesList;
    try {
      judgesList = JSON.parse(judgesRaw);
    } catch {
      window.alert('Hakim verisi okunamadı.');
      return;
    }

    const restored = revertFolderDistributionsFromJudges(judgesList, list);

    localStorage.setItem('judges', JSON.stringify(restored));
    window.dispatchEvent(new Event('judges-localstorage-updated'));

    removeFolderFromState(folderId);
  };

  const handleSetActiveFolder = (folderId) => {
    setActiveFolder(folderId);
  };

  const removeDistributionFromFolders = (folderId, distributionIndex) =>
    folders.map((folder) => {
      if (folder.id === folderId) {
        const updatedDistributions = folder.distributions.filter(
          (_, index) => index !== distributionIndex
        );
        return { ...folder, distributions: updatedDistributions };
      }
      return folder;
    });

  const persistFolders = (updatedFolders) => {
    setFolders(updatedFolders);
    localStorage.setItem('distributionFolders', JSON.stringify(updatedFolders));
  };

  const handleDeleteDistributionAndRevertScores = (folderId, distributionIndex) => {
    const folder = folders.find((f) => f.id === folderId);
    const distribution = folder?.distributions?.[distributionIndex];
    if (!distribution) return;

    const cls = classifyDistributionScoreRevert(distribution);
    if (cls === 'skip') {
      if (
        !window.confirm(
          'Bu kayıt, kaydedilirken genel hakim puan listesine uygulanmadı (yalnızca tevzi listesi güncellendi). Silme işlemi hakim puanlarını değiştirmez.\n\nDevam edilsin mi?'
        )
      ) {
        return;
      }
      persistFolders(removeDistributionFromFolders(folderId, distributionIndex));
      return;
    }

    if (cls === 'none') {
      window.alert(
        'Bu kayıtta bu tevzinin puan etkisini hesaplamak için yeterli bilgi yok. İsterseniz yalnızca "Kaydı sil" ile listeyi güncelleyebilirsiniz.'
      );
      return;
    }

    if (
      !window.confirm(
        'Bu tevzi kaydı silinecek ve o tevzi kaydedilirken hakimlere eklenen puan artışı ile bu tevzide atanan dosya adedi, şu anki hakim puanlarından ve dosya sayılarından düşülecek.\n\nBaşka kayıtlı tevziler aynı kalır; yalnızca bu kaydın katkısı geri alınır.\n\nDevam edilsin mi?'
      )
    ) {
      return;
    }

    const judgesRaw = localStorage.getItem('judges');
    if (!judgesRaw) {
      window.alert('Kayıtlı hakim listesi bulunamadı; puan güncellenemedi.');
      return;
    }

    let judgesList;
    try {
      judgesList = JSON.parse(judgesRaw);
    } catch {
      window.alert('Hakim verisi okunamadı.');
      return;
    }

    const restored = revertDistributionDeltaFromJudges(judgesList, distribution);

    localStorage.setItem('judges', JSON.stringify(restored));
    window.dispatchEvent(new Event('judges-localstorage-updated'));

    persistFolders(removeDistributionFromFolders(folderId, distributionIndex));
  };

  const handleShowAllResults = (folderId) => {
    const folder = folders.find((f) => f.id === folderId);
    if (!folder) return;

    // Her dosyaya geldiği tevzinin adını (`sourceFileName`) işaretle ki birleşik
    // görünümde dosya detay modalı doğru "Kaynak liste:" gösterebilsin.
    //
    // ÖNEMLİ: formatRow her tevzide id=1,2,3,... şeklinde başlattığı için farklı
    // tevzilerden gelen dosyaların id'leri ÇAKIŞIR. Manuel taşımada `setDistributedFiles
    // map(f => f.id === fileId ? ... : f)` aynı id'li tüm dosyaları günceller →
    // hedef hakime çift dosya eklenir. Bunu önlemek için birleşik modda her dosyaya
    // `${distIdx}-${originalId}` formatında unique bir id atıyoruz. files ve
    // distributedFiles aynı mapping'i kullandığı için eşleşme korunur.
    const allFiles = folder.distributions.flatMap((dist, distIdx) =>
      (dist.files || []).map((f) => ({
        ...f,
        id: `${distIdx}-${f.id}`,
        sourceFileName: f.sourceFileName || dist.fileName,
      }))
    );
    const allDistributedFiles = folder.distributions.flatMap((dist, distIdx) =>
      (dist.distributedFiles || []).map((f) => ({
        ...f,
        id: `${distIdx}-${f.id}`,
        sourceFileName: f.sourceFileName || dist.fileName,
      }))
    );

    const combinedSummary = folder.distributions.reduce((acc, curr) => {
      if (curr.summaryReport) {
        acc.toplamDosyaSayisi =
          (acc.toplamDosyaSayisi || 0) + (curr.summaryReport.toplamDosyaSayisi || 0);
        acc.toplamDosyaPuani =
          (acc.toplamDosyaPuani || 0) + (curr.summaryReport.toplamDosyaPuani || 0);

        if (curr.summaryReport.hakimRaporlari) {
          curr.summaryReport.hakimRaporlari.forEach((hakim) => {
            if (!acc.hakimRaporlari) acc.hakimRaporlari = [];
            const existingHakim = acc.hakimRaporlari.find(
              (h) => h.hakimAdi === hakim.hakimAdi
            );
            if (existingHakim) {
              existingHakim.dosyaSayisi += hakim.dosyaSayisi;
              existingHakim.toplamPuan += hakim.toplamPuan;
            } else {
              acc.hakimRaporlari.push({ ...hakim });
            }
          });
        }
      }
      return acc;
    }, {});

    navigate('/', {
      state: {
        loadAllResults: true,
        folderId,
        allFiles,
        allDistributedFiles,
        combinedSummary,
      },
    });
  };

  const handleViewDistribution = (folderId, distributionIndex) => {
    navigate('/', {
      state: {
        loadDistribution: true,
        folderId,
        distributionIndex,
      },
    });
  };

  const totals = useMemo(() => {
    const n = folders.length;
    const dist = folders.reduce(
      (s, f) => s + (Array.isArray(f.distributions) ? f.distributions.length : 0),
      0
    );
    return { folderCount: n, distributionCount: dist };
  }, [folders]);

  return (
    <div className="min-h-[calc(100vh-5rem)] bg-gradient-to-b from-slate-50 via-white to-slate-50/90 px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <header className="mb-8 border-b border-slate-200/80 pb-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-600 text-white shadow-md shadow-indigo-500/25">
                  <Archive className="h-5 w-5" aria-hidden />
                </span>
                <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                  Kayıtlı tevziler
                </h1>
              </div>
              <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-600">
                Dağıtımları klasörler halinde saklayın; tek kaydı veya klasörün tamamını Dosya
                Yönetimi ekranında yeniden açabilirsiniz. Silme ile geri alma arasındaki farkı aşağıda
                özetledik.
              </p>
            </div>
          </div>
        </header>

        <div
          className="mb-8 rounded-xl border border-red-200/90 bg-red-50/90 px-4 py-3 text-sm text-red-950 shadow-sm ring-1 ring-red-900/5"
          role="note"
        >
          <div className="flex gap-2">
            <Trash2 className="mt-0.5 h-5 w-5 shrink-0 text-red-600" aria-hidden />
            <div>
              <p className="font-semibold text-red-900">Sil</p>
              <p className="mt-1 leading-relaxed text-red-950/95">
                Kaydı veya klasörü siler ve işlenmiş tevzilerde eklenen puan ile bu tevzilerdeki
                dosya adedini hakim kayıtlarından düşer (klasörde önce son tevzi). İşlenmemiş
                kayıtlarda puan etkisi olmaz.
              </p>
            </div>
          </div>
        </div>

        <section className="mb-8 grid gap-3 sm:grid-cols-2" aria-label="Özet">
          <div className="rounded-xl border border-slate-200/90 bg-white p-4 shadow-sm ring-1 ring-slate-900/5">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-700">
                <Folder className="h-4 w-4" aria-hidden />
              </span>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  Klasör
                </p>
                <p className="text-2xl font-bold tabular-nums text-slate-900">{totals.folderCount}</p>
              </div>
            </div>
          </div>
          <div className="rounded-xl border border-slate-200/90 bg-white p-4 shadow-sm ring-1 ring-slate-900/5">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700">
                <FileText className="h-4 w-4" aria-hidden />
              </span>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  Toplam kayıt
                </p>
                <p className="text-2xl font-bold tabular-nums text-slate-900">
                  {totals.distributionCount}
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="mb-10 rounded-2xl border border-slate-200/90 bg-white p-5 shadow-lg shadow-slate-900/5 ring-1 ring-slate-900/5">
          <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
            <Sparkles className="h-5 w-5 text-indigo-600 sm:shrink-0" aria-hidden />
            <h2 className="text-lg font-semibold text-slate-900">Yeni klasör</h2>
          </div>
          <form
            className="flex flex-col gap-3 sm:flex-row sm:items-stretch"
            onSubmit={(e) => {
              e.preventDefault();
              handleAddFolder();
            }}
          >
            <label htmlFor="new-folder-name" className="sr-only">
              Klasör adı
            </label>
            <input
              id="new-folder-name"
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              className="min-w-0 flex-1 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-inner shadow-slate-900/5 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
              placeholder="Örn: Ocak 2026 — Ceza dairesi"
              autoComplete="off"
            />
            <button
              type="submit"
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 py-3 text-sm font-semibold text-white shadow-md shadow-indigo-500/25 transition hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
            >
              <Plus className="h-4 w-4" aria-hidden />
              Ekle
            </button>
          </form>
        </section>

        <section aria-labelledby="folders-heading">
          <div className="mb-6 flex items-center gap-2">
            <Info className="h-5 w-5 text-slate-400" aria-hidden />
            <h2 id="folders-heading" className="text-lg font-semibold text-slate-900">
              Klasörler ve kayıtlar
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
            {folders.map((folder) => {
              const isActive = activeFolder === folder.id;
              const count = folder.distributions?.length ?? 0;
              return (
                <article
                  key={folder.id}
                  className={`flex flex-col overflow-hidden rounded-2xl border bg-white shadow-lg shadow-slate-900/5 ring-1 transition hover:shadow-xl ${
                    isActive
                      ? 'border-indigo-400 ring-indigo-500/20'
                      : 'border-slate-200/90 ring-slate-900/5'
                  }`}
                >
                  <div
                    className={`border-b px-4 py-4 ${
                      isActive
                        ? 'border-indigo-100 bg-gradient-to-br from-indigo-50/90 to-white'
                        : 'border-slate-100 bg-gradient-to-br from-slate-50/90 to-white'
                    }`}
                  >
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="flex min-w-0 flex-1 items-start gap-2">
                        <span className="mt-0.5 shrink-0 text-indigo-600" aria-hidden>
                          {isActive ? (
                            <FolderOpen className="h-6 w-6" />
                          ) : (
                            <Folder className="h-6 w-6 text-slate-400" />
                          )}
                        </span>
                        <div className="min-w-0 flex-1">
                          <h3
                            className="truncate text-lg font-semibold text-slate-900"
                            title={folder.name}
                          >
                            {folder.name}
                          </h3>
                          <p className="mt-1 text-xs text-slate-500">
                            {count === 0
                              ? 'Henüz kayıt yok'
                              : `${count} kayıt`}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => handleSetActiveFolder(folder.id)}
                          className={`rounded-full px-3 py-1 text-xs font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 ${
                            isActive
                              ? 'cursor-default bg-indigo-600 text-white shadow-sm'
                              : 'border border-slate-200 bg-white text-slate-600 hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-800'
                          }`}
                          aria-current={isActive ? 'true' : undefined}
                        >
                          {isActive ? 'Aktif' : 'Aktif yap'}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteFolderAndRevertScores(folder.id)}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200/80 bg-white text-red-600 transition hover:border-red-200 hover:bg-red-50 hover:text-red-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
                          title="Klasörü sil ve hakim kayıtlarına etkisini geri al"
                          aria-label={`${folder.name} klasörünü sil`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-1 flex-col p-4">
                    <button
                      type="button"
                      onClick={() => handleShowAllResults(folder.id)}
                      disabled={count === 0}
                      className="mb-4 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-900 shadow-sm transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2"
                    >
                      <Eye className="h-4 w-4" aria-hidden />
                      Tüm sonuçları göster
                    </button>

                    {count === 0 ? (
                      <div className="flex flex-1 flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50/50 px-4 py-10 text-center">
                        <AlertTriangle className="mb-2 h-8 w-8 text-slate-300" aria-hidden />
                        <p className="text-sm text-slate-500">
                          Bu klasörde henüz tevzi kaydı yok. Dosya Yönetimi’nde dağıtım kaydettiğinizde
                          burada listelenir.
                        </p>
                      </div>
                    ) : (
                      <ul className="space-y-2">
                        {folder.distributions.map((distribution, index) => (
                          <li key={`${folder.id}-${index}`}>
                            <div className="flex items-center gap-2 rounded-xl border border-slate-100 bg-slate-50/80 p-2 transition hover:border-slate-200 hover:bg-white">
                              <button
                                type="button"
                                onClick={() => handleViewDistribution(folder.id, index)}
                                className="flex min-w-0 flex-1 items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm text-slate-800 transition hover:bg-indigo-50 hover:text-indigo-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                              >
                                <FileText className="h-4 w-4 shrink-0 text-indigo-500" aria-hidden />
                                <span className="truncate font-medium">
                                  {distribution.fileName || `Dağıtım #${index + 1}`}
                                </span>
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  handleDeleteDistributionAndRevertScores(folder.id, index)
                                }
                                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200/80 bg-white text-red-600 transition hover:border-red-200 hover:bg-red-50 hover:text-red-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
                                title="Kaydı sil ve hakim kayıtlarına etkisini geri al"
                                aria-label="Kaydı sil"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
};

export default SavedDistributions;
